import { Suspense } from "react";
import { useAtomValue } from "jotai";
import { useNodeId } from "@xyflow/react";
import { ToggleSwitch } from "@zapier/design-system";
import {
  zapMetadataAtomFamily_SUSPENDS,
  assetAccessAtomFamily_SUSPENDS,
  isDraftOrTemplateNodeAtomFamily,
  isViewOnlyModeAtom,
} from "@zapier/canvas-state";

import { ErrorBoundary } from "../../ErrorBoundary";
import { UnlinkedStatusIndicator } from "../../UnlinkedStatusIndicator";
import { useAssetToggle } from "../../../hooks/useAssetToggle";
import { ToggleContainer } from "../ToggleContainer";
import { LoadingToggle } from "../LoadingToggle";
import {
  getAssetStatus,
  getDraftTooltipContent,
  getStatusTooltipContent,
} from "../utils";

interface ZapToggleProps {
  zapId: string | null;
}

function ZapToggleSuspended({ zapId }: { zapId: string }) {
  const zapMetadata = useAtomValue(zapMetadataAtomFamily_SUSPENDS(zapId));
  const { isAssetInaccessible } = useAtomValue(
    assetAccessAtomFamily_SUSPENDS({
      assetId: zapId,
      assetType: "zap",
    }),
  );
  const isViewOnlyMode = useAtomValue(isViewOnlyModeAtom);

  const { toggleAsset, isLoading } = useAssetToggle({
    assetType: "zap",
    assetId: zapId,
  });

  if (isAssetInaccessible) {
    return null;
  }

  const assetStatus = getAssetStatus(zapMetadata, "zap");

  if (!assetStatus) {
    return null;
  }

  const { isEnabled, isDraft } = assetStatus;

  if (isDraft) {
    return (
      <ToggleContainer
        tooltipContent={getDraftTooltipContent("zap")}
        ariaLabel="Zap is in draft state"
      >
        <ToggleSwitch disabled showDisabledTooltip={false} />
      </ToggleContainer>
    );
  }

  return (
    <ToggleContainer
      tooltipContent={getStatusTooltipContent("zap", isEnabled)}
      ariaLabel={getStatusTooltipContent("zap", isEnabled)}
    >
      <ToggleSwitch
        isLoading={isLoading}
        checked={isEnabled}
        onChange={() => toggleAsset(isEnabled)}
        disabled={isViewOnlyMode}
        showDisabledTooltip={false}
      />
    </ToggleContainer>
  );
}

export function ZapToggle({ zapId }: ZapToggleProps) {
  const nodeId = useNodeId();

  const isDraftOrTemplateNode = useAtomValue(
    isDraftOrTemplateNodeAtomFamily(nodeId),
  );

  if (isDraftOrTemplateNode) {
    return null;
  }

  if (!zapId) {
    return <UnlinkedStatusIndicator />;
  }

  return (
    <ErrorBoundary renderNullOnError>
      <Suspense fallback={<LoadingToggle />}>
        <ZapToggleSuspended zapId={zapId} />
      </Suspense>
    </ErrorBoundary>
  );
}
